/**
 * Ruft `run`, sobald `getElement()` **stillsteht**: Seine Lage ist `stableFrames` Bilder lang
 * dieselbe, und weder an ihm noch an einem seiner Vorfahren läuft eine Animation. Höchstens
 * `maxWait` ms — dann trotzdem, damit nichts hängen bleibt.
 *
 * Für Popover, die an etwas hängen, das gerade erst hereinfährt: Die Aufgaben-Details gleiten beim
 * Öffnen herein (`task-details-enter`), daneben schrumpft die Liste. Ein Popover, das in dieser
 * Zeit aufgeht, steht an der Stelle von vorhin und springt erst später nach (gemeldet 30.09.2026:
 * die Glocke an der Zeile).
 *
 * Gibt eine Abbruch-Funktion zurück.
 *
 * @example
 * useEffect(() => whenStill(() => chipRef.current, () => setOpen(true)), [])
 */
export function whenStill(
  getElement: () => Element | null,
  run: () => void,
  {
    stableFrames = 5,
    maxWait = 1500,
    requestFrame = (cb: FrameRequestCallback) => requestAnimationFrame(cb),
    cancelFrame = (id: number) => cancelAnimationFrame(id),
    now = () => performance.now(),
  }: {
    stableFrames?: number
    maxWait?: number
    requestFrame?: (cb: FrameRequestCallback) => number
    cancelFrame?: (id: number) => void
    now?: () => number
  } = {},
): () => void {
  const started = now()
  let frame = 0
  let still = 0
  let last: string | null = null

  const animating = (el: Element): boolean => {
    for (let node: Element | null = el; node; node = node.parentElement) {
      // jsdom kennt `getAnimations` nicht — dort gilt allein die Lage.
      if (typeof node.getAnimations !== 'function') return false
      if (node.getAnimations().some(a => a.playState === 'running')) return true
    }
    return false
  }

  const tick = () => {
    const el = getElement()
    if (el) {
      const r = el.getBoundingClientRect()
      const at = `${r.left},${r.top},${r.width},${r.height}`
      still = at === last && !animating(el) ? still + 1 : 0
      last = at
    }
    if (still >= stableFrames || now() - started >= maxWait) {
      run()
      return
    }
    frame = requestFrame(tick)
  }
  frame = requestFrame(tick)

  return () => cancelFrame(frame)
}
